import { useContext } from "react";
import { motion } from "framer-motion";
import { Moon, Wifi, Car, Coffee } from "lucide-react";
import { LanguageContext } from "../App";

const icons = [Moon, Wifi, Car, Coffee];

export default function Experience() {
  const { t } = useContext(LanguageContext);

  return (
    <section
      id="experience"
      data-testid="experience-section"
      className="bg-[#FDFBF7] py-24 md:py-32 px-6"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16 md:mb-20 text-center max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="font-['Manrope',sans-serif] text-xs uppercase tracking-[0.3em] text-[#7B907F] mb-5"
            data-testid="experience-overline"
          >
            {t.experience.overline}
          </motion.p>

          {/* First phrase — smaller italic tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-['Cormorant_Garamond',serif] text-xl md:text-2xl font-light italic text-[#8A847F] mb-3"
            data-testid="experience-headline"
          >
            {t.experience.headline}
          </motion.p>

          {/* Second phrase — big serif headline */}
          <motion.h2
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="font-['Cormorant_Garamond',serif] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#2A2726] tracking-tight leading-tight"
            data-testid="experience-subheadline"
          >
            {t.experience.subheadline}
          </motion.h2>
        </div>

        {/* 2x2 grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#DDD8CE]">
          {t.experience.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="bg-[#FDFBF7] p-6 sm:p-10 md:p-14 flex flex-col gap-4 group hover:bg-[#F5F1EA] transition-colors duration-300"
                data-testid={`experience-item-${i}`}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#DDD8CE] group-hover:border-[#7B907F] transition-colors duration-300">
                  <Icon size={20} strokeWidth={1.5} className="text-[#7B907F]" />
                </div>
                <div>
                  <h3 className="font-['Cormorant_Garamond',serif] text-2xl md:text-3xl font-light text-[#2A2726] mb-3 leading-tight">
                    {item.title}
                  </h3>
                  <p className="font-['Manrope',sans-serif] text-sm text-[#5C5754] leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
